import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Phone, Loader2, Check, Lock, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Massive WOW button that triggers the AI receptionist test flow:
 *  - creates a real test lead in the workspace
 *  - fires the existing email/notification workflow
 *  - shows toast feedback + a success state on the card
 *
 * `onTested` is called after a successful run so the parent dashboard can
 * refresh the checklist progress.
 */
export function TestReceptionistButton({ onTested }) {
  const { language } = useLanguage();
  const { workspace } = useAuth();
  const navigate = useNavigate();
  const tf = (fr, en) => (language === 'fr' ? fr : en);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  // Source of truth = workspace.plan (same field shown in Admin / Quotas).
  // The derived `sms_enabled` flag can fall out of sync if a workspace was
  // upgraded before the plan-sync migration ran, which is why we don't trust
  // it here. Starter -> upsell card; Pro & Enterprise -> live test button.
  const planId = String(workspace?.plan || 'starter').toLowerCase();
  const receptionistUnlocked = planId === 'pro' || planId === 'enterprise';

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/onboarding/test-receptionist');
      setSuccess(data);
      toast.success(
        tf('Votre receptionniste IA vient de creer un prospect test', 'Your AI receptionist just created a test lead'),
        {
          description: data.workflow_triggered
            ? tf(`Notification envoyee a ${data.notified_email}`, `Notification sent to ${data.notified_email}`)
            : tf('Configurez votre email dans Reglages pour recevoir les alertes', 'Configure email in Settings to get alerts'),
        }
      );
      if (onTested) onTested();
    } catch (err) {
      toast.error(err.response?.data?.detail || tf('Erreur lors du test', 'Test failed'));
    } finally {
      setLoading(false);
    }
  };

  // ─── Starter variant: upsell card, NO real test action ───
  // The AI receptionist is locked behind Pro. We keep the same hero card
  // shape so the layout doesn't shift, but swap copy + CTA to drive upgrade.
  if (!receptionistUnlocked) {
    return (
      <Card
        className="overflow-hidden border-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white shadow-xl"
        data-testid="test-receptionist-card-upsell"
      >
        <CardContent className="p-5 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="hidden md:flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-amber-500/15 backdrop-blur">
              <Lock className="h-10 w-10 text-amber-300" />
            </div>

            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 text-amber-300">
                <Sparkles className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-widest">
                  {tf('Inclus dans Pro', 'Included in Pro')}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-black leading-tight">
                {tf('Receptionniste IA + SMS automatises', 'AI receptionist + automated SMS')}
              </h2>
              <p className="text-sm md:text-base text-slate-200/90 max-w-xl">
                {tf(
                  'Repondez aux prospects 24/7, confirmez les rendez-vous par SMS et capturez les appels manques. Disponible des le forfait Pro (97 $ / mois).',
                  'Answer leads 24/7, confirm appointments via SMS, capture missed calls. Available from the Pro plan ($97 / mo).'
                )}
              </p>
            </div>

            <Button
              onClick={() => navigate('/settings?tab=billing')}
              size="lg"
              className="w-full md:w-auto h-14 md:h-16 px-6 md:px-8 text-base md:text-lg font-bold rounded-2xl bg-amber-400 text-slate-900 hover:bg-amber-300 shadow-lg shrink-0"
              data-testid="test-receptionist-upgrade-btn"
            >
              <ArrowUpRight className="mr-2 h-5 w-5" />
              {tf('Passer a Pro', 'Upgrade to Pro')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Pro / Enterprise variant: real test runner (unchanged) ───

  return (
    <Card
      className="overflow-hidden border-0 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 text-white shadow-xl"
      data-testid="test-receptionist-card"
    >
      <CardContent className="p-5 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="hidden md:flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
            <Phone className="h-10 w-10" />
          </div>

          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 text-amber-100">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-widest">
                {tf('Demonstration', 'Demo')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-black leading-tight">
              {tf('Tester mon receptionniste IA', 'Test my AI receptionist')}
            </h2>
            <p className="text-sm md:text-base text-amber-50/95 max-w-xl">
              {success
                ? tf(
                    'Bravo chef ! Votre bureau virtuel a recu un nouveau prospect. Allez le voir dans la liste ci-dessous.',
                    'Well done! Your virtual office just received a new lead. Check the list below.'
                  )
                : tf(
                    'Un clic et votre IA cree un faux prospect, vous envoie un email et active toutes vos automatisations.',
                    'One click and your AI creates a sample lead, emails you and runs all your automations.'
                  )}
            </p>
          </div>

          <Button
            onClick={run}
            disabled={loading}
            size="lg"
            className="w-full md:w-auto h-14 md:h-16 px-6 md:px-8 text-base md:text-lg font-bold rounded-2xl bg-white text-orange-600 hover:bg-amber-50 shadow-lg shrink-0"
            data-testid="test-receptionist-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {tf('Lancement...', 'Running...')}
              </>
            ) : success ? (
              <>
                <Check className="mr-2 h-5 w-5" />
                {tf('Relancer le test', 'Run test again')}
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-5 w-5" />
                {tf('Lancer le test', 'Start the test')}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
